import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Button, Divider, Loader } from "semantic-ui-react";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import constants from "utils/constants";
import util from "utils/utils";

import SettingActions from "components/lib/SettingActions";

import TermsCheckbox from "./TermsCheckbox";

const AgreeToTerms = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const [searchParams] = useSearchParams();
  const returnUrl = useMemo(() => {
    const next = searchParams.get("next") || "";
    try {
      return atob(next) || "/";
    } catch (e) {
      return "/";
    }
  }, [searchParams]);

  const [hasAgreed, setHasAgreed] = useState(false);
  const [agreedCustomTerms, setAgreedCustomTerms] = useState(false);
  const [organisation, setOrganisation] = useState(user?.ownerOrganisation);

  const [loading, setLoading] = useState(false);

  const requiresTermsAgreement = !hasAgreed;
  const requiresCustomTermsAgreement =
    organisation?.customTerms?.enabled ? !agreedCustomTerms : false;

  useEffect(() => {
    if (!organisation) {
      const code = util.code();
      api.organisations.getByCode(
        code,
        (org) => {
          setOrganisation(org);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    }
  }, [organisation]);

  const onAgree = useCallback(() => {
    if (!hasAgreed) {
      toast.error("Please agree to the terms before continuing");
      return;
    }
    if (user) {
      setLoading(true);
      api.auth.agreeToTerms(
        constants.termsVersion,
        user?.ownerOrganisation?.customTerms?.version,
        (agreements) => {
          dispatch(actions.user.agreeTerms(agreements));
          setLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setLoading(false);
        },
      );
    } else {
      try {
        window.location.href = returnUrl;
      } catch (e) {
        toast.error("Something went wrong. Please try logging in again.");
      }
    }
  }, [hasAgreed, dispatch, user, returnUrl]);

  if (!organisation) {
    return <Loader active inline="centered" />;
  }

  return (
    <>
      <h3>{t("entry.terms.title")}</h3>
      <p>
        {user ?
          t("entry.terms.description")
        : t("entry.terms.newUserDescription")}
      </p>
      <TermsCheckbox
        organisation={organisation}
        agreed={hasAgreed}
        onAgree={(agreed) => setHasAgreed(agreed)}
        agreedCustomTerms={agreedCustomTerms}
        onAgreeCustomTerms={(agreed) => setAgreedCustomTerms(agreed)}
      />
      <Divider hidden />
      <SettingActions>
        <Button
          primary
          content="Continue"
          onClick={onAgree}
          loading={loading}
          disabled={requiresTermsAgreement || requiresCustomTermsAgreement}
        />
      </SettingActions>
    </>
  );
};

export default AgreeToTerms;
